import { useState } from "react";
import { defaultModel, Model } from "./Model";
import { ItemListadoServiciosGuarderia } from "../../../services/guarderiaServicios";
import { CiudadResumida, Sector } from "../../../services/paisesServicios";
import { RespuestaBuscarPropietario } from "../../../services/propietariosServicios";
import { ServicioTransporte } from "../../../services/transporteServicios";

// En este custom hook se definen mutaciones del modelo en casa paso del Wizard.
// Muchas mutaciones implican el reseteo de pasos posteriores.
//
// Por ejemplo: Si se cambia la mascota seleccionada, se deben resetear las fechas de
// la reserva porque la disponibilidad es distinta para cada mascota.
export const usePasoReservaGuarderia = (model: Model) => {
  const [currentModel, setCurrentModel] = useState<Model>(model);

  const seleccionarMascota = (datosAccion: {
    idMascota: string;
    nombreMascota: string;
    idPropietario: string;
    nombresPropietario: string;
  }) => {
    setCurrentModel((prevModel) => {
      // Reiniciando propiedades, las propidades de pasos futuros quedan indefinidas
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.SeleccionarMascota
      );

      const updatedModel: Model = {
        ...modelReseteado,
        idMascota: datosAccion.idMascota,
        nombreMascota: datosAccion.nombreMascota,
        idPropietario: datosAccion.idPropietario,
        nombresPropietario: datosAccion.nombresPropietario,
      };
      return updatedModel;
    });
  };

  const definirDatosPropietario = (datosAccion: {
    datosPropietario: RespuestaBuscarPropietario
  }) => {
    setCurrentModel((prevModel) => {
      // Reiniciando propiedades, las propidades de pasos futuros quedan indefinidas
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.SeleccionarMascota
      );

      const updatedModel: Model = {
        ...modelReseteado,
        datosPropietario: datosAccion.datosPropietario,
      };
      return updatedModel;
    });
  };

  const definirDiasIndividuales = (datosAccion: {
    diasIndividualesGuarderia: number;
    valorFinalDiaIndividual: number;
    servicioDiaIndividual?: ItemListadoServiciosGuarderia;
  }) => {
    setCurrentModel((prevModel) => {
      // Las propiedades de pasos futuros quedan indefinidas
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.DatosBasicos
      );

      const updatedModel: Model = {
        ...modelReseteado,
        diasIndividualesGuarderia: datosAccion.diasIndividualesGuarderia,
        valorFinalDiaIndividual: datosAccion.valorFinalDiaIndividual,
        servicioDiaIndividual: datosAccion.servicioDiaIndividual,
      };
      return updatedModel;
    });
  };

  const definirContrataTransporte = (datosAccion: {
    contrataTransporte: boolean;
  }) => {
    setCurrentModel((prevModel) => {
      // Las propiedades de pasos futuros quedan indefinidas
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.DatosBasicos
      );

      const contrata = datosAccion.contrataTransporte;

      const updatedModel: Model = {
        ...modelReseteado,
        // Propiedades de este paso (Datos Basicos) y accion
        contrataTransporte: contrata,
        fechasTransporte: contrata ? prevModel.fechasProgramadas : []
      };
      return updatedModel;
    });
  };

  const definirCiudadOrigen = (datosAccion: {
    ciudadOrigen: CiudadResumida | undefined;
  }) => {
    setCurrentModel((prevModel) => {
      // Las propiedades de pasos futuros quedan indefinidas
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.DatosBasicos
      );

      const updatedModel: Model = {
        ...modelReseteado,
        ciudadOrigen: datosAccion.ciudadOrigen
      };
      return updatedModel;
    });
  };

  const seleccionarPlan = (datosAccion: {
    plan: ItemListadoServiciosGuarderia;
  }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.DatosBasicos
      );

      const updatedModel: Model = {
        ...modelReseteado,
        // Propiedades de este paso (Datos Basicos) y accion
        plan: datosAccion.plan,
        valorFinalPlan: datosAccion.plan.precioTotal,
      };
      return updatedModel;
    });
  };

  const definirFechasGuarderia = (datosAccion: {
     fechas: Date[]
  }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.FechasGuarderia
      );

      const updatedModel: Model = {
        ...modelReseteado,
        fechasProgramadas: datosAccion.fechas,
        // Por defecto, se contratan todas las fechas en el transporte
        fechasTransporte: prevModel.contrataTransporte ? datosAccion.fechas : []
      };
      return updatedModel;
    });
  };

  const definirDireccionTransporte = (datosAccion: {
    direccion: string | undefined,
  }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.DatosTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        direccionTransporte: datosAccion.direccion,
      };
      return updatedModel;
    });
  }

  const definirSectorTransporte = (datosAccion: {
    sector: Sector | undefined
  }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.DatosTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        sectorTransporte: datosAccion.sector
      };
      return updatedModel;
    });
  }

  const definirContrataIda = (datosAccion: {
    contrata: boolean
  }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.DatosTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        contrataIda: datosAccion.contrata
      };
      return updatedModel;
    });
  }

  const definirContrataVuelta = (datosAccion: {
    contrata: boolean
  }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.DatosTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        contrataVuelta: datosAccion.contrata
      };
      return updatedModel;
    });
  }

  const definirFechasTransporte = (datosAccion: {
    fechas: Date[]
  }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.FechasTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        fechasTransporte: datosAccion.fechas
      };
      return updatedModel;
    });
  }

  const definirTarifaIda = (datosAccion: {
    tarifaIda: ServicioTransporte,
  }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.CalculoPrecioTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        tarifaIda: datosAccion.tarifaIda,
        valorFinalIda: datosAccion.tarifaIda.precio
      };
      return updatedModel;
    });
  }

  const definirTarifaVuelta = (datosAccion: {
    tarifaVuelta: ServicioTransporte
  }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.CalculoPrecioTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        tarifaVuelta: datosAccion.tarifaVuelta,
        valorFinalVuelta: datosAccion.tarifaVuelta.precio
      };
      return updatedModel;
    });
  }

  const definirTarifaIdaVuelta = (datosAccion: {
    tarifaIdaVuelta: ServicioTransporte
  }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.CalculoPrecioTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        tarifaIdaVuelta: datosAccion.tarifaIdaVuelta,
        valorFinalIdaVuelta: datosAccion.tarifaIdaVuelta.precio
      };
      return updatedModel;
    });
  }

  const reiniciarTarifasTransporte = () => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.CalculoPrecioTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        tarifaIda: undefined,
        valorFinalIda: undefined,
        tarifaVuelta: undefined,
        valorFinalVuelta: undefined,
        tarifaIdaVuelta: undefined,
        valorFinalIdaVuelta: undefined
      };
      return updatedModel;
    });
  }

  const definirUsarTarifasPorTrayecto = (datosAccion: {
    usarTarifasPorTrayecto: boolean
  }) => {
    setCurrentModel((prevModel) => {
      let modelReseteado = resetearValoresPasosPosteriores(
        prevModel,
        IndicesPasos.CalculoPrecioTransporte
      );

      const updatedModel: Model = {
        ...modelReseteado,
        usarTarifasPorTrayecto: datosAccion.usarTarifasPorTrayecto
      };
      return updatedModel;
    });
  }

  return {
    currentModel,
    seleccionarMascota,
    definirDatosPropietario,
    definirDiasIndividuales,
    definirContrataTransporte,
    definirCiudadOrigen,
    seleccionarPlan,
    definirFechasGuarderia,
    definirDireccionTransporte,
    definirSectorTransporte,
    definirContrataIda,
    definirContrataVuelta,
    definirFechasTransporte,
    definirTarifaIda,
    definirTarifaVuelta,
    definirTarifaIdaVuelta,
    reiniciarTarifasTransporte,
    definirUsarTarifasPorTrayecto
  };
};

const resetearSeleccionarMascota = (model: Model): Model => {
  return {
    ...model,
    idMascota: defaultModel.idMascota,
    nombreMascota: defaultModel.nombreMascota,
    idPropietario: defaultModel.idPropietario,
    nombresPropietario: defaultModel.nombresPropietario,
    datosPropietario: defaultModel.datosPropietario,
  };
};

const resetearDatosBasicos = (model: Model): Model => {
  return {
    ...model,
    contrataTransporte: defaultModel.contrataTransporte,
    diasIndividualesGuarderia: defaultModel.diasIndividualesGuarderia,
    valorFinalDiaIndividual: defaultModel.valorFinalDiaIndividual,
    servicioDiaIndividual: defaultModel.servicioDiaIndividual,
    plan: defaultModel.plan,
    valorFinalPlan: defaultModel.valorFinalPlan,
    ciudadOrigen: defaultModel.ciudadOrigen
  };
};

const resetearFechasGuarderia = (model: Model): Model => {
  return {
    ...model,
    fechasProgramadas: defaultModel.fechasProgramadas,
  };
};

const resetearDatosTransporte = (model: Model): Model => {
  return {
    ...model,
    sectorTransporte: defaultModel.sectorTransporte,
    direccionTransporte: defaultModel.direccionTransporte,
    fechasTransporte: defaultModel.fechasTransporte,
    contrataIda: defaultModel.contrataIda,
    contrataVuelta: defaultModel.contrataVuelta,
  };
};

const resetearFechasTransporte = (model: Model): Model => {
  return {
    ...model,
    fechasTransporte: defaultModel.fechasTransporte,
  };
};

const resetearCalculoPrecioTransporte = (model: Model): Model => {
  return {
    ...model,
    tarifaIda: defaultModel.tarifaIda,
    valorFinalIda: defaultModel.valorFinalIda,
    tarifaVuelta: defaultModel.tarifaVuelta,
    valorFinalVuelta: defaultModel.valorFinalVuelta,
    tarifaIdaVuelta: defaultModel.tarifaIdaVuelta,
    valorFinalIdaVuelta: defaultModel.valorFinalIdaVuelta,
    usarTarifasPorTrayecto: defaultModel.usarTarifasPorTrayecto
  };
};

const resetearResumen = (model: Model): Model => {
  return {
    ...model,
    descuento: defaultModel.descuento,
  };
};

const resetsPasos: Array<(model: Model) => Model> = [
  resetearSeleccionarMascota,
  resetearDatosBasicos,
  resetearFechasGuarderia,
  resetearDatosTransporte,
  resetearFechasTransporte,
  resetearCalculoPrecioTransporte,
  resetearResumen,
];

const IndicesPasos = {
  SeleccionarMascota: 0,
  DatosBasicos: 1,
  FechasGuarderia: 2,
  DatosTransporte: 3,
  FechasTransporte: 4,
  CalculoPrecioTransporte: 5,
  Resumen: 6,
};

const resetearValoresPasosPosteriores = (
  model: Model,
  indicePaso: number
): Model => {
  let modelActualizado: Model = model;
  for (let i = indicePaso + 1; i < resetsPasos.length; i++) {
    modelActualizado = resetsPasos[i](modelActualizado);
  }
  return modelActualizado;
};
